import { optionSpecialInstructionsAtom } from "@/atoms";
import { NoteTextarea } from "@/components/forms/NoteTextarea";
import { BottomSheet } from "@/components/modals/BottomSheet";
import { Button, Icon, SheetBody, SheetFooter, Text } from "@/components/ui";
import { Edit, Plus } from "@/icons";
import { cn } from "@/lib/cn";
import * as Dialog from "@radix-ui/react-dialog";
import { useAtom } from "jotai";
import { useEffect, useState, type MouseEvent } from "react";

const MAX_LENGTH = 200;

type OptionSpecialInstructionChipProps = {
  optionGroupId: string;
  optionId: string;
  optionName: string;
  fullWidthOnMobile?: boolean;
};

export default function OptionSpecialInstructionChip({
  optionGroupId,
  optionId,
  optionName,
  fullWidthOnMobile = false,
}: OptionSpecialInstructionChipProps) {
  const [optionSpecialInstructions, setOptionSpecialInstructions] = useAtom(optionSpecialInstructionsAtom);
  const savedValue = optionSpecialInstructions[optionGroupId]?.[optionId] ?? "";

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(savedValue);

  useEffect(() => {
    if (open) setDraft(savedValue);
  }, [open, savedValue]);

  function handleChipClick(event: MouseEvent) {
    event.stopPropagation();
    setOpen(true);
  }

  function handleSave() {
    const trimmed = draft.trim();
    setOptionSpecialInstructions((state) => {
      const groupState = { ...(state[optionGroupId] ?? {}) };
      if (trimmed) {
        groupState[optionId] = trimmed;
      } else {
        delete groupState[optionId];
      }
      const next = { ...state, [optionGroupId]: groupState };
      if (Object.keys(groupState).length === 0) {
        delete next[optionGroupId];
      }
      return next;
    });
    setOpen(false);
  }

  const hasNote = savedValue.length > 0;

  return (
    <>
      <Button
        variant={hasNote ? "outline" : "dashed-soft"}
        size="sm"
        weight="medium"
        selected={hasNote}
        onClick={handleChipClick}
        aria-label={hasNote ? `Editar observação para ${optionName}` : `Adicionar observação para ${optionName}`}
        className={cn("max-w-full", fullWidthOnMobile && "w-full justify-center md:w-auto md:justify-start")}
      >
        {hasNote ? (
          <>
            <Icon as={Edit} size="sm" weight="fill" />
            <span className="truncate text-left">{savedValue}</span>
          </>
        ) : (
          <>
            <Icon as={Plus} size="sm" weight="bold" />
            <span>Adicionar observação</span>
          </>
        )}
      </Button>

      <BottomSheet
        modal
        open={open}
        onOpenChange={setOpen}
        title="Observação"
        onClose={() => setOpen(false)}
        width="form"
        headerBordered
      >
        <SheetBody className="flex flex-col gap-3 pb-2">
          <div className="flex flex-col gap-1">
            <Text tone="muted">
              Algum detalhe específico para{" "}
              <Text as="strong" weight="bold" tone="default">
                {optionName}
              </Text>
              ?
            </Text>
            <Text size="sm" tone="muted">
              Ex: sem cebola, bem passado, sem sal.
            </Text>
          </div>
          <NoteTextarea
            autoFocus
            value={draft}
            onChange={setDraft}
            placeholder="Escreva sua observação aqui"
            maxLength={MAX_LENGTH}
            className="p-3"
            counterClassName="text-xs self-end"
          />
        </SheetBody>
        <SheetFooter className="flex-row items-stretch">
          <Dialog.Close asChild>
            <Button variant="outline" tone="neutral" className="flex-1">
              Voltar
            </Button>
          </Dialog.Close>
          <Button onClick={handleSave} className="flex-1">
            Salvar
          </Button>
        </SheetFooter>
      </BottomSheet>
    </>
  );
}
